/* eslint-disable @typescript-eslint/no-explicit-any */

import { HttpMethod } from "../common/Enums";
import { Constants } from '../common/Constants';
import { createApiErrorFromResponse, logError } from '../utils/errorHandling';

export abstract class BaseApiProvider {
    public readonly apiUrl: string = Constants.VITE_APP_API_URL;
    private _authProvider: { getToken: () => Promise<string> };

    public get authProvider() {
        return this._authProvider;
    }

    public set authProvider(value: { getToken: () => Promise<string> }) {
        this._authProvider = value;
    }

    protected constructor(authProviderInstance: { getToken: () => Promise<string> }) {
        this._authProvider = authProviderInstance;
    }

    protected async _send(apiUrl: string, endpoint: string, request: RequestInit): Promise<any> {
        try {
            const response = await fetch(this._url(apiUrl, endpoint), request);
            
            if (!response.ok) {
                const apiError = await createApiErrorFromResponse(response);
                logError(apiError, `API Request: ${request.method} ${endpoint}`);
                throw apiError;
            }
            
            if (response.status === 204) {
                return null; 
            }
            
            return await response.json();
        } catch (error) {
            // If it's already an ApiError, just re-throw it
            if (error instanceof Error && 'isApiError' in error) {
                throw error;
            }
            
            // For network errors or other unexpected errors, log and re-throw
            logError(error, `API Request: ${request.method} ${endpoint}`);
            throw error; 
        }
    }


    protected _headers(token: string, contentType: string = 'application/json'): HeadersInit {
        return {
            'Authorization': `Bearer ${token}`,
            'Content-Type': contentType,
        };
    }

    protected _url(apiUrl: string, endpoint: string): string {
        return `${apiUrl}${endpoint}`;
    }

    protected async createRequestInit(token: string, method: HttpMethod, body?: any, contentType?: string): Promise<RequestInit> {
        const headers = this._headers(token, contentType || 'application/json');
        const requestInit: RequestInit = {
            method: method,
            headers: headers
        };
        if (body) {
            if (body instanceof FormData) {
                delete (headers as Record<string, string>)['Content-Type']; 
                requestInit.body = body;
            } else {
                requestInit.body = JSON.stringify(body);
            }
        }
        return requestInit;
    }
}
